import { jsx, Fragment, jsxs } from 'react/jsx-runtime';
import { useState, useEffect, useContext, useRef, useCallback, cloneElement } from 'react';
import { Box, Typography, LinearProgress, useTheme, useMediaQuery, CircularProgress, Tooltip } from '@mui/material';
import { useApi, API, AuthContext, IconContext } from '@plumeuk/shapeshift-identity';
import 'x2js';
import { makeStyles } from 'tss-react/mui';
import { StyleSheet, Document, Page, Image as Image$1, View, Text, PDFDownloadLink } from '@react-pdf/renderer';

/* eslint-disable no-console */
console.log.bind(null) ;
console.warn.bind(null) ;
console.error.bind(null) ;

function toDate(argument) {
    if (isDate(argument)) {
        return new Date(argument.getTime());
    }
    else if (typeof argument === "number") {
        return new Date(argument);
    }
    else {
        return new Date(NaN);
    }
}
function compareAsc(dateLeft, dateRight) {
    const _dateLeft = toDate(dateLeft);
    const _dateRight = toDate(dateRight);
    const diff = _dateLeft.getTime() - _dateRight.getTime();
    if (diff < 0) {
        return -1;
    }
    else if (diff > 0) {
        return 1;
        // Return 0 if diff is 0; return NaN if diff is NaN
    }
    else {
        return diff;
    }
}
function isDate(value) {
    return (value instanceof Date ||
        (typeof value === "object" &&
            Object.prototype.toString.call(value) === "[object Date]"));
}

//Get module in tree
//Default latest active, if request passed returns the matching module or undefined
const getModule = (course, request, includeUnavailable) => {
    const n = (modules) => {
        let module;
        modules.forEach(e => {
            if (module)
                return;
            if (e.type !== "section" && ((request && request.slug == e.slug && request.type == e.type)
                || (!request && !e.complete && (includeUnavailable || !e.availableFrom || e.availableFrom < new Date())))) {
                module = e;
                return;
            }
            else if (e.modules) {
                module = n(e.modules);
                if (module)
                    return;
            }
        });
        return module;
    };
    if (!course || !course.modules)
        return;
    return n(course?.modules);
};

const loadImage = (url) => new Promise((res, rej) => {
    const img = new Image();
    img.addEventListener("load", () => res(img));
    img.addEventListener("error", (err) => rej(err));
    img.src = url;
});
const buildPngFromSvg = async (src) => {
    const res = await fetch(src);
    const img = await res.text();
    const svgData = new Blob([img], { type: "image/svg+xml" });
    const DOMURL = window.URL || window.webkitURL || window;
    const b64str = DOMURL.createObjectURL(svgData);
    //CANVAS
    const mImg = await loadImage(b64str);
    const imgW = mImg.naturalWidth;
    const imgH = mImg.naturalHeight;
    const scale = window.devicePixelRatio * 2;
    const canv = document.createElement("canvas");
    canv.width = imgW;
    canv.height = imgH;
    const ctx = canv.getContext("2d");
    canv["style"]["width"] = `${Math.round(imgW / scale)}px`;
    canv["style"]["height"] = `${Math.round(imgH / scale)}px`;
    canv["style"]["margin"] = "0";
    canv["style"]["padding"] = "0";
    ctx?.scale(scale, scale);
    ctx?.drawImage(mImg, 0, 0, Math.round(imgW / scale), Math.round(imgH / scale));
    const imgSrc = canv.toDataURL();
    return imgSrc ? imgSrc : "";
};

const useCourseCurriculum = (courseSlug, data) => {
    const [apiResult, getData] = useApi(data || !courseSlug ? null : API.course(courseSlug), data);
    const [course, setCourse] = useState(data);
    useEffect(() => setCourse(apiResult.data), [apiResult.data]);
    useEffect(() => setCourse(data), [data]);
    const setModuleComplete = (moduleRequest, status) => {
        //handle internal update
        if (!data && course) {
            setCourse(prev => {
                const module = getModule(course, moduleRequest);
                if (!prev || !module || module.complete == status)
                    return prev;
                if (module.progression !== false) {
                    prev.modulesCompleteCount += (status ? 1 : -1);
                    prev.percentCompleted = Math.round((prev.modulesCompleteCount / prev.modulesCount) * 100);
                }
                module.complete = status;
                return { ...prev };
            });
        }
    };
    const setModule = (moduleRequest, prop, value) => {
        if (!data && course) {
            setCourse(prev => {
                const module = getModule(course, moduleRequest);
                if (prop === "complete")
                    throw new Error("Use setModuleComplete instead");
                if (!prev || !module || module[prop] == value)
                    return prev;
                module[prop] = value;
                return { ...prev };
            });
        }
    };
    const getCourse = (slugOverride, configOverride) => {
        const slug = slugOverride ?? courseSlug;
        if (!slug)
            return;
        getData(configOverride
            ? { ...configOverride, url: API.course(slug) }
            : API.course(slugOverride ?? slug));
    };
    return { course, apiResult, getCourse, setCourse, setModuleComplete, setModule };
};

const useStyles$4 = makeStyles()((theme) => ({
    courseProgressionSummary: {
        marginBottom: "25px",
        overflow: "hidden"
    },
    courseTitle: {
        color: "white",
        marginBottom: "10px",
        fontSize: "16px",
        [theme.breakpoints.down("sm")]: {
            fontSize: "18px"
        }
    },
    progressContainer: {
        color: "white"
    },
    percentLabel: {
        display: "inline-block"
    },
    progressBar: {
        marginRight: "15px",
        display: "inline-block",
        padding: 0,
        border: "none",
        width: "calc(100% - 60px)"
    }
}));
const CourseProgressionSummary = ({ className, course, ...props }) => {
    const { classes, cx } = useStyles$4();
    if (!course)
        return jsx(Fragment, {});
    return (jsxs(Box, { className: cx(classes.courseProgressionSummary, className), ...props, children: [jsx(Typography, { variant: "body1", className: classes.courseTitle, children: course.title }), jsxs(Box, { className: classes.progressContainer, children: [jsx(LinearProgress, { variant: "determinate", className: classes.progressBar, value: course.percentCompleted }), jsxs(Typography, { className: classes.percentLabel, variant: "body1", children: [course.percentCompleted, "%"] })] })] }));
};

const useSetModuleCompletion = () => {
    const [apiResult, getData] = useApi(null);
    const setComplete = (courseSlug, moduleSlug, type, status, config) => getData({
        ...(config ?? {}),
        url: API.moduleComplete(courseSlug, moduleSlug, type, status),
        method: "POST",
        cache: false
    });
    return { response: apiResult.data, apiResult, setComplete };
};

const useMq = () => {
    const theme = useTheme();
    const isMobileMq = useMediaQuery(`(max-width:${theme.breakpoints.values.sm - 1}px)`);
    const isTabletMq = useMediaQuery(`(min-width:${theme.breakpoints.values.sm}px) and (max-width:${theme.breakpoints.values.md}px)`);
    const isDesktopMq = useMediaQuery(`(min-width:${theme?.breakpoints.values.md + 1}px)`);
    return {
        mobile: isMobileMq,
        tablet: isTabletMq,
        desktop: isDesktopMq
    };
};

//Hook returns url path to server resources
//With strapi local dev path is relative
function useFileBaseUrl() {
    const { config } = useContext(AuthContext);
    const baseFileUrl = process.env.NODE_ENV === "production" ? "" : (config?.baseApiUrl ?? "");
    const getUrl = (e) => {
        if (e.startsWith("http"))
            return e;
        return baseFileUrl + e;
    };
    return { baseFileUrl, getUrl };
}

const useCourseCertificate = (courseSlug, data) => {
    const [apiResult, getData] = useApi(data || !courseSlug ? null : API.courseCertificate(courseSlug), data);
    const [certificate, setCertificate] = useState();
    useEffect(() => setCertificate(apiResult.data), [apiResult.data]);
    useEffect(() => setCertificate(data), [data]);
    const getCertificate = (slugOverride, configOverride) => {
        const slug = slugOverride ?? courseSlug;
        if (!slug)
            return;
        getData(configOverride
            ? { ...configOverride, url: API.courseCertificate(slug) }
            : API.courseCertificate(slug));
    };
    useEffect(() => data && setCertificate(data), [data]);
    return { certificate, apiResult, getCertificate, setCertificate };
};

const useStyles$3 = makeStyles()((theme, { isSection, hasChildren, isLocked, isOpen, isActive, level, levelIndentSize }) => ({
    courseSideMenuModule: {},
    contentContainer: {
        borderRadius: "10px",
        padding: isActive ? "8px 0 8px 0px" : "9px 1px 9px 1px",
        border: isActive ? `1px solid ${theme.palette.secondary.main}` : "",
        marginRight: "10px",
        marginLeft: (level * levelIndentSize) + "px",
        display: "flex",
        cursor: "pointer",
        "& svg": {
            color: theme.palette.secondary.main,
            marginRight: "8px",
            marginLeft: "8px",
            fontSize: "18px"
        },
        "& > *": !hasChildren && {
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            height: "auto"
        }
    },
    title: {
        wordBreak: "break-word",
        userSelect: "none",
        display: hasChildren ? "flex" : "inline-block",
        width: "100%",
        padding: isSection ? "20px 0 10px 0" : 0,
        justifyContent: "space-between",
        opacity: isLocked ? 0.5 : 1,
        "&:hover svg": {
            transform: `scale(1.3) rotate(${isOpen ? "0deg" : "180deg"})`
        }
    },
    titleText: {
        fontSize: isSection ? "16px" : "14px",
        [theme.breakpoints.down("sm")]: {
            fontSize: isSection ? "18px" : "16px"
        }
    },
    moduleTitle: {
        position: "relative"
    },
    modulesParentContainer: {
        transition: "height 0.2s ease-out, opacity 0.25s",
        overflow: "hidden",
        height: 0,
        opacity: 0,
        ...(isOpen && {
            overflow: "visible",
            opacity: 1,
            transition: "0.25s"
        })
    },
    icon: {
        textAlign: "center",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        justifyContent: "start",
        width: "45px",
        transition: "transform .2s ease-in-out",
        "&:hover": !isLocked && {
            transform: "scale(1.2)"
        }
    },
    expandIcon: {
        color: theme.palette.secondary.main,
        fontSize: "27px",
        transform: `rotate(${isOpen ? "0deg" : "180deg"})`,
        transition: "transform 0.2s linear"
    },
    iconLoading: {
        "& svg": {
            color: theme.palette.secondary.main,
            position: "static",
            padding: 0
        }
    },
    isLocked: {
        pointerEvents: "none"
    }
}));
const getOpenStatus = (module, activeModuleSlug) => !!(module.modules && module.modules.find(e => e.slug === activeModuleSlug)) ?? false;
const hasIncompleteChildrenCheck = (module) => {
    const check = (module) => {
        if (!module?.modules || module.modules.length === 0)
            return false;
        if (module.modules.filter(e => e.type !== "section" && !e.complete).length > 0)
            return true;
        for (let i = 0; i < module.modules.length; i++) {
            if (check(module?.modules[i]))
                return true;
        }
        return false;
    };
    return check(module);
};
const CourseSideMenuModule = ({ typeIcons, courseSlug, className, module, activeModuleSlug, disableTooltip, setActive, level = 0, levelIndentSize = 10, onClick, onModuleComplete, completeActionOverride, componentOverrides, ...props }) => {
    const mq = useMq();
    const { icon } = useContext(IconContext);
    const currentDate = new Date();
    const fromDate = module.availableFrom ? new Date(module.availableFrom) : null;
    const dueDate = module.dueDate ? new Date(module.dueDate) : null;
    const isSection = module.type === "section", hasChildren = !!module.modules, hasIncompleteChildren = hasIncompleteChildrenCheck(module);
    const isLocked = !!(fromDate && isDate(fromDate) && compareAsc(fromDate, currentDate) > 0);
    const isDue = !!(dueDate && isDate(dueDate) && compareAsc(dueDate, currentDate) > 0);
    const isOverdue = !!(dueDate && isDate(dueDate) && compareAsc(dueDate, currentDate) < 0);
    const isComplete = module.complete || false;
    const hasTooltip = isLocked || isDue || isComplete || isOverdue;
    const modulesContainer = useRef(null);
    const [isActive, setIsActive] = useState(activeModuleSlug === module.slug);
    const [isOpen, setIsOpen] = useState(getOpenStatus(module, activeModuleSlug ?? ""));
    const { classes, cx } = useStyles$3({ isSection, hasChildren, isLocked, hasTooltip, isComplete, isDue, isOverdue, isOpen, isActive, level, levelIndentSize });
    const { setComplete, apiResult: setCompleteResult, response: setCompleteResponse } = useSetModuleCompletion();
    const activeIconRef = useRef(null);
    const accordianIconRef = useRef(null);
    const [containerHeight, setContainerHeight] = useState("0px");
    useEffect(() => {
        if (!modulesContainer.current)
            return;
        const containerHeight = modulesContainer.current.offsetHeight;
        setContainerHeight(prev => {
            //No change state
            if (prev === "auto" && isOpen || (prev === "0px" && !isOpen)) {
                return prev;
            }
            //Catch 
            if (prev !== "auto" && !isOpen) {
                setContainerHeight("0px");
            }
            if (prev === "auto" || isOpen) {
                if (isOpen && prev !== "auto")
                    setTimeout(() => {
                        setContainerHeight("auto");
                    }, 350);
                return containerHeight + "px";
            }
            return "0px";
        });
    }, [isOpen, containerHeight]);
    useEffect(() => {
        if (isActive)
            setIsOpen(true);
    }, [isActive]);
    useEffect(() => {
        setIsActive(module.slug === activeModuleSlug);
        setIsOpen(prev => {
            const newStatus = getOpenStatus(module, activeModuleSlug ?? "");
            return newStatus ? true : prev;
        });
    }, [activeModuleSlug, module.slug]);
    const handleClick = (e) => {
        //prevent action if clicking active icon
        if (activeIconRef.current?.contains(e?.target))
            return;
        //prevent action if clicking accordian icon
        if (accordianIconRef.current?.contains(e?.target))
            return;
        //prevent action if mobile and locked
        if (isLocked && mq.mobile)
            return;
        onClick?.(e);
        if (!isSection)
            setActive(module);
    };
    const handleComplete = completeActionOverride ?? useCallback(() => {
        if (!isLocked)
            setComplete(courseSlug, module.slug, module.type, !module.complete);
    }, [module.complete, module.slug, isLocked]);
    useEffect(() => {
        if (!setCompleteResult.isLoading && setCompleteResponse && setCompleteResult.statusCode === 200) {
            onModuleComplete(module, setCompleteResponse.complete);
        }
    }, [setCompleteResult, setCompleteResponse]);
    const infoArray = [
        isSection ? "isSection" : "",
        hasChildren ? "hasChildren" : "",
        isLocked ? "isLocked" : "",
        isOpen ? "isOpen" : "",
        isActive ? "isActive" : "",
        level ? "level" : ""
    ];
    const Title = () => {
        const titleText = jsx("span", { children: module.title });
        if (!disableTooltip && hasTooltip) {
            let tooltipContent = "";
            const monthNames = ["January", "February", "March", "April", "May", "June",
                "July", "August", "September", "October", "November", "December"];
            if (isLocked && fromDate) {
                tooltipContent = `Available on ${fromDate.getDate()} ${monthNames[fromDate.getMonth()]} ${fromDate.getFullYear()}`;
            }
            else if (isComplete) {
                tooltipContent = "Completed";
            }
            else if (isDue && dueDate) {
                tooltipContent = `Due on ${dueDate.getDate()} ${monthNames[dueDate.getMonth()]} ${dueDate.getFullYear()}`;
            }
            else if (isOverdue) {
                tooltipContent = "Overdue";
            }
            return (jsx(Tooltip, { title: tooltipContent, placement: mq.mobile ? "bottom" : "right-start", enterTouchDelay: 0, leaveTouchDelay: 2000, children: titleText }));
        }
        return titleText;
    };
    const dataTestId = `course-side-menu-module-${module.type}-${module.id ?? 0}-${level ?? 0}`;
    return (jsxs(Box, { className: cx(classes.courseSideMenuModule, className, module.type, module.type !== "section" ? "module" : undefined, infoArray), "data-test-id": dataTestId, ...props, children: [jsxs(Box, { "data-test-id": `${dataTestId}-content-container`, className: classes.contentContainer, onClick: handleClick, children: [componentOverrides?.(module)?.header ?? jsx(Fragment, {}), componentOverrides?.(module)?.icon ?? (!isSection && jsxs("div", { ref: activeIconRef, onClick: () => handleComplete(), "data-test-id": `${dataTestId}-complete-btn`, className: cx(classes.icon, module.complete ? "module-complete" : "module-incomplete", hasIncompleteChildren ? "module-incomplete-children" : "module-complete-children"), children: [!setCompleteResult.isLoading && !isLocked && module.progression !== false && (module.complete ?
                                hasIncompleteChildren ?
                                    icon("checkCircleOutlinedIcon")
                                    : icon("checkIcon", { style: { color: "white" } })
                                : icon("circleIcon", { style: { opacity: 0.5 } })), setCompleteResult.isLoading && jsx("div", { children: jsx(CircularProgress, { size: "16px", className: classes.iconLoading }) }), isLocked && icon("lockIcon", { style: { opacity: 0.5 } })] })), typeIcons?.find(e => e.type === module.type)?.icon, jsxs(Box, { lang: "en", className: classes.title, "data-test-id": `${dataTestId}-expand-section`, onClick: () => isSection && setIsOpen(prev => !prev), children: [jsxs(Box, { className: classes.moduleTitle, children: [componentOverrides?.(module)?.preTitle ?? jsx(Fragment, {}), jsx(Title, {}), componentOverrides?.(module)?.footer ?? jsx(Fragment, {})] }), hasChildren &&
                                icon("expandMoreIcon", {
                                    "data-test-id": `${dataTestId}-expand-more-btn`,
                                    ref: accordianIconRef,
                                    className: classes.expandIcon,
                                    onClick: () => !isSection && setIsOpen(prev => !prev)
                                })] }), componentOverrides?.(module)?.rightAccessory ?? jsx(Fragment, {})] }), jsx(Box, { className: cx(classes.modulesParentContainer, isActive ? "active" : ""), style: { height: containerHeight }, children: jsx("div", { ref: modulesContainer, children: module?.modules?.map((e, i) => jsx(CourseSideMenuModule, { courseSlug: courseSlug, typeIcons: typeIcons, module: e, level: level + 1, disableTooltip: disableTooltip, levelIndentSize: levelIndentSize, onModuleComplete: onModuleComplete, completeActionOverride: completeActionOverride, activeModuleSlug: activeModuleSlug, componentOverrides: componentOverrides, setActive: setActive }, "module-" + i)) }) })] }));
};

const useStyles$2 = makeStyles()((_theme) => ({
    courseSideBar: {
        padding: "25px"
    }
}));
const courseSideMenuContent = ({ typeIcons, courseSlug, data, activeModule: activeModuleProp, levelIndentSize, disableTooltip, onActiveChange, onModuleComplete, onModuleForbidden, className, children, componentOverrides, ...props }) => {
    const { classes, cx } = useStyles$2();
    const [activeModule, setActiveModule] = useState();
    const { course, setModuleComplete } = useCourseCurriculum(courseSlug, data);
    useEffect(() => {
        if (activeModuleProp)
            setActiveModule(() => getModule(course, activeModuleProp));
        else
            setActiveModule(undefined);
    }, [activeModuleProp, course]);
    const handleActiveModuleSelect = (e) => {
        if (course?.linearProgression && e.complete == false) {
            const nextModule = getModule(course);
            if (nextModule?.slug !== e?.slug || nextModule?.type !== e?.type) {
                onModuleForbidden?.(e, nextModule);
                return;
            }
        }
        onActiveChange?.(e);
    };
    const handleSetModuleComplete = (e, status) => {
        if (e.type === "section")
            return;
        setModuleComplete({ slug: e.slug, type: e.type }, status);
    };
    return (jsxs(Box, { className: cx(classes.courseSideBar, className), ...props, children: [jsx(CourseProgressionSummary, { course: course }), !children && courseSlug && course?.modules
                .filter(e => e.type !== "section" || (e.modules?.length ?? 0) > 0)
                .map((e, i) => (jsx(CourseSideMenuModule, { courseSlug: courseSlug, module: e, typeIcons: typeIcons, levelIndentSize: levelIndentSize, disableTooltip: disableTooltip, activeModuleSlug: activeModule?.slug, onModuleComplete: onModuleComplete ?? handleSetModuleComplete, setActive: handleActiveModuleSelect, componentOverrides: componentOverrides?.module }, "module-" + i))), componentOverrides?.footer ?? jsx(Fragment, {})] }));
};

const CourseCertificateDataProvider = ({ children, courseSlug, data }) => {
    const dataHook = useCourseCertificate(courseSlug, data);
    useEffect(() => {
        if (courseSlug && !data)
            dataHook.getCertificate(courseSlug);
    }, [courseSlug]);
    return children?.(dataHook)
        ?? jsxs("div", { children: ["Please implement children to handle course certificate ", dataHook.certificate?.printedTitle ?? "...loading"] });
};

const useStyles$1 = makeStyles()((theme) => ({
    file: {
        marginBottom: "20px",
        flexWrap: "wrap",
        display: "flex",
        borderRadius: "10px",
        backgroundColor: "#BCBCBD33",
        padding: "20px",
        "& a": {
            textDecoration: "none"
        },
        [theme.breakpoints.down("sm")]: {
            backgroundColor: "#fff",
            paddingBottom: "5px"
        }
    },
    fileIcon: {
        borderRadius: "15px",
        backgroundColor: theme.palette.secondary.main,
        color: "white",
        margin: "10px",
        padding: "15px",
        height: "fit-content",
        width: "fit-content",
        [theme.breakpoints.down("sm")]: {
            margin: "0"
        }
    },
    contentContainer: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        flexGrow: 5,
        flexBasis: "10px",
        textAlign: "left",
        maxWidth: "calc(100% - 60px)",
        margin: "10px 30px",
        [theme.breakpoints.down("sm")]: {
            margin: "0 0 0 30px",
            wordBreak: "break-all"
        }
    },
    downloadButton: {
        cursor: "pointer",
        textAlign: "center",
        color: theme.palette.secondary.main,
        marginRight: "20px",
        margin: "10px",
        [theme.breakpoints.down("sm")]: {
            flexBasis: "100%",
            display: "flex",
            flexDirection: "row-reverse",
            justifyContent: "center",
            "& *": {
                display: "inline"
            },
            "& svg": {
                marginLeft: "5px"
            },
            marginTop: "20px",
            paddingTop: "10px",
            borderTop: "solid 1px " + theme.palette.secondary.main
        },
        [theme.breakpoints.up("sm")]: {
            borderLeft: "solid 1px #BCBCBD",
            paddingLeft: "30px"
        }
    },
    title: {},
    description: {
        color: "#2A2A2F",
        [theme.breakpoints.down("sm")]: {
            flexBasis: "100%",
            paddingTop: "20px"
        }
    },
    fileDownloadAnchor: {
        [theme.breakpoints.down("sm")]: {
            flexBasis: "100%"
        }
    }
}));
const File = ({ className, file, componentOverrides, actionOverrideDownload, ...props }) => {
    const { classes, cx } = useStyles$1();
    const { getUrl } = useFileBaseUrl();
    const mq = useMq();
    const { fileCopyIcon, downloadForOfflineIcon } = useContext(IconContext);
    const downloadBtn = (jsxs(Box, { className: classes.downloadButton, children: [componentOverrides?.downloadIcon ?? downloadForOfflineIcon, jsx(Typography, { children: "Download" })] }));
    const alternativeText = file.alternativeText || file.caption ? (jsx(Typography, { className: classes.description, children: file.caption ?? file.alternativeText })) : jsx(Fragment, {});
    return (jsxs(Box, { className: cx(classes.file, className), ...props, children: [jsx(Box, { className: classes.fileIcon, children: componentOverrides?.fileIcon ?? fileCopyIcon }), jsxs(Box, { className: classes.contentContainer, children: [jsx(Typography, { variant: "h6", className: classes.title, children: file.name }), !mq.mobile && alternativeText] }), mq.mobile && alternativeText, actionOverrideDownload
                ? cloneElement(downloadBtn, { onClick: actionOverrideDownload })
                : (jsx("a", { target: "_blank", rel: "noreferrer", href: getUrl(file.url), download: "", className: classes.fileDownloadAnchor, children: downloadBtn }))] }));
};

const styles = StyleSheet.create({
    page: {
        fontFamily: "Helvetica",
        flexDirection: "column",
        backgroundColor: "#ffffff",
        textAlign: "center",
        fontSize: 12
    },
    section: {
        justifyContent: "space-around",
        margin: 10,
        padding: 10,
        flexGrow: 1,
        lineHeight: 1.5
    },
    printedName: {
        fontFamily: "Helvetica-Bold",
        fontSize: 50,
        fontWeight: 900
    },
    h2: {
        fontSize: 20
    },
    institution: {
        paddingTop: 20,
        color: "#d1d1d1",
        fontFamily: "Helvetica-Bold"
    },
    backgroundImg: {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: "100%",
        height: "100%",
        objectFit: "cover"
    },
    centerSection: {
        margin: 50
    },
    bottomSection: {
        flexDirection: "row"
    },
    linedSection: {
        flexGrow: 1
    },
    linedSectionValueContainer: {
        borderBottom: "1px solid black"
    },
    linedSectionValue: {
        alignSelf: "center"
    },
    linedSectionLabelContainer: {},
    linedSectionLabel: {
        marginTop: 5,
        letterSpacing: 2,
        fontSize: 12,
        color: "grey",
        alignSelf: "center"
    },
    spacer: {
        width: 20
    }
});
const CourseCertificateReactPDFDocument = ({ certificate, ...props }) => {
    const [imgSrc, setImgSrc] = useState("");
    const svgToPng = useCallback(async (url) => {
        const mSrc = await buildPngFromSvg(url);
        setImgSrc(mSrc);
    }, []);
    useEffect(() => {
        if (certificate?.backgroundUrl?.endsWith(".svg")) {
            svgToPng(certificate.backgroundUrl);
        }
    }, [certificate]);
    return jsx(Document, { ...props, children: jsxs(Page, { size: "LETTER", orientation: "landscape", style: styles.page, children: [jsx(Image$1, { style: styles.backgroundImg, src: !imgSrc ? certificate.backgroundUrl : imgSrc }), jsx(View, { style: styles.section, children: jsx(Text, { style: [styles.h2, styles.institution], children: certificate.institution }) }), jsxs(View, { style: [styles.section, styles.centerSection], children: [jsx(Text, { style: styles.h2, children: "Certificate of Completion" }), jsx(Text, { children: "is hereby granted to" }), jsx(Text, { style: styles.printedName, children: certificate.printedName }), jsx(Text, { children: "to certify completion of" }), jsx(Text, { style: styles.h2, children: certificate.printedTitle })] }), jsxs(View, { style: [styles.section, styles.bottomSection], children: [jsx(View, { style: styles.linedSection }), jsxs(View, { style: styles.linedSection, children: [jsx(View, { style: styles.linedSectionValueContainer, children: jsx(Text, { style: styles.linedSectionValue, children: certificate.completionDate.toDateString() }) }), jsx(View, { style: styles.linedSectionLabelContainer, children: jsx(Text, { style: styles.linedSectionLabel, children: "Date" }) })] }), jsx(View, { style: styles.spacer }), certificate.instructor && jsxs(Fragment, { children: [jsxs(View, { style: styles.linedSection, children: [jsx(View, { style: styles.linedSectionValueContainer, children: jsx(Text, { style: styles.linedSectionValue, children: certificate.instructor }) }), jsx(View, { style: styles.linedSectionLabelContainer, children: jsx(Text, { style: styles.linedSectionLabel, children: "Instructor" }) })] }), jsx(View, { style: styles.spacer })] }), certificate.expiryDate && jsxs(View, { style: styles.linedSection, children: [jsx(View, { style: styles.linedSectionValueContainer, children: jsx(Text, { style: styles.linedSectionValue, children: certificate.expiryDate.toDateString() }) }), jsx(View, { style: styles.linedSectionLabelContainer, children: jsx(Text, { style: styles.linedSectionLabel, children: "Expiry date" }) })] }), jsx(View, { style: styles.linedSection })] })] }) });
};

const useStyles = makeStyles()((theme) => ({
    courseCertificateFile: {},
    file: {
        margin: "30px 0",
        background: "#BCBCBD33",
        [theme.breakpoints.down("sm")]: {
            "& [class$='fileIcon']": {
                display: "none"
            },
            "& [class$='contentContainer']": {
                textAlign: "center"
            }
        }
    }
}));
const CourseCertificateFile = ({ className, courseSlug, data, componentOverrides, ...props }) => {
    const { classes, cx } = useStyles();
    const pdfDownloadAnchorRef = useRef(null);
    const file = {
        "name": "Course certificate"
    };
    const handleOnClick = useCallback((_e) => pdfDownloadAnchorRef?.current?.click(), []);
    const generateTitle = (courseTitle) => {
        const title = courseTitle.trim().split(" ").map(e => `${e[0].toLocaleUpperCase()}${e.substring(1)}`).join(" ");
        return `${title} Certificate`;
    };
    const fileProps = {
        "data-test-id": `course-${courseSlug}-certificate-file`,
        actionOverrideDownload: handleOnClick,
        file: file,
        className: classes.file
    };
    return (jsxs(Box, { className: cx(classes.courseCertificateFile, className), ...props, children: [jsx(CourseCertificateDataProvider, { data: data, courseSlug: courseSlug, children: ({ certificate }) => (certificate
                    ? jsx(PDFDownloadLink, { fileName: generateTitle(certificate.printedTitle), style: { textDecoration: "none" }, document: componentOverrides?.document?.(certificate)
                            ?? jsx(CourseCertificateReactPDFDocument, { certificate: certificate }), children: jsx("div", { ref: pdfDownloadAnchorRef }) })
                    : jsx(Fragment, {})) }), componentOverrides?.file ? componentOverrides.file(fileProps) : jsx(File, { ...fileProps })] }));
};

export { CourseCertificateFile as CourseCertificate, CourseProgressionSummary, courseSideMenuContent as CourseSideMenuContent };
